import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { useAccount } from './AccountContext';
import { useSelection } from '../selection/SelectionContext';
import { brand } from '../theme';
import MintAvatar from '../ui/MintAvatar';

const shimmer = {
  background: `linear-gradient(90deg,#F3ECE1 0%,${brand.canvasSunk} 40%,#F3ECE1 80%)`,
  backgroundSize: '320px 100%',
  animation: 'chd-shimmer 1.4s linear infinite',
} as const;

export interface BalanceRow {
  id: string;
  amount: string;
  label: string;
  symbol: string;
  chain: string;
  usdPrice: string;
  decimals: number;
  image?: string | null;
}

export function multiplyUsd(amount: string, usdPrice: string): string | null {
  if (!amount.trim() || Number.isNaN(Number(amount))) return null;
  const a = Number(amount);
  const p = Number(usdPrice);
  if (!Number.isFinite(a) || !Number.isFinite(p)) return null;
  return (a * p).toFixed(2);
}

export function usdNotional(amount: string, usdPrice: string): number {
  const a = Number(amount);
  const p = Number(usdPrice);
  if (!Number.isFinite(a) || !Number.isFinite(p)) return 0;
  return a * p;
}

/** Always show 8 fraction digits (including trailing zeros), matching the reference UI. */
export function formatTokenAmount(amount: string, _decimals: number): string {
  void _decimals;
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0.00000000';
  return n.toFixed(8);
}

export function formatBalanceAmount(
  row: Pick<BalanceRow, 'amount' | 'usdPrice' | 'decimals'>,
  displayInUsd: boolean,
): string {
  if (displayInUsd) {
    const usd = multiplyUsd(row.amount, row.usdPrice);
    return usd != null ? `$${usd}` : '—';
  }
  return formatTokenAmount(row.amount, row.decimals);
}

/**
 * Highest USD notional mint, or the first catalog row when every balance is
 * zero. Returns null when the caller should keep the current selection
 * (all-zero catalog and current mint still exists).
 */
export function pickDefaultBalanceRow(
  rows: BalanceRow[],
  currentMintId: number | undefined,
): BalanceRow | null {
  if (rows.length === 0) return null;

  let best = rows[0];
  let bestUsd = usdNotional(best.amount, best.usdPrice);
  for (let i = 1; i < rows.length; i++) {
    const usd = usdNotional(rows[i].amount, rows[i].usdPrice);
    if (usd > bestUsd) {
      best = rows[i];
      bestUsd = usd;
    }
  }

  if (bestUsd > 0) return best;
  if (
    currentMintId != null &&
    rows.some((row) => row.id === String(currentMintId))
  ) {
    return null;
  }
  return rows[0];
}

/**
 * Every mainnet mint from the catalog, joined to the customer ledger
 * (missing balance → "0"). Not org treasury.
 */
export function useCustomerBalanceRows(): {
  balances: BalanceRow[];
  loading: boolean;
  error: string | null;
  showSkeleton: boolean;
} {
  const { account, loading, error } = useAccount();
  const { networks, loadingNetworks } = useSelection();

  const balances = useMemo(() => {
    const ledger = account?.customerBalances ?? {};
    const rows: BalanceRow[] = [];
    for (const group of networks) {
      for (const token of group.tokens) {
        const id = String(token.mintId);
        rows.push({
          id,
          amount: ledger[id] ?? '0',
          label: `${token.symbol} · ${group.chain}`,
          symbol: token.symbol,
          chain: group.chain,
          usdPrice: token.usdPrice,
          decimals: token.decimals,
          image: token.image ?? null,
        });
      }
    }
    return rows.sort((a, b) => a.label.localeCompare(b.label));
  }, [account, networks]);

  const showSkeleton =
    (loading && !account) || (loadingNetworks && networks.length === 0);

  return {
    balances,
    loading,
    error,
    showSkeleton,
  };
}

export interface BalanceListProps {
  compact?: boolean;
  /** When true, show amount × catalog usdPrice as $X.XX. */
  displayInUsd?: boolean;
  selectedMintId?: number;
  onSelect?: (row: BalanceRow) => void;
}

/** Compact customer balance list for the header wallet popover. */
export default function BalanceList({
  compact = false,
  displayInUsd = false,
  selectedMintId,
  onSelect,
}: BalanceListProps) {
  const { balances, error, showSkeleton } = useCustomerBalanceRows();
  const avatarSize = compact ? 26 : 30;
  const selectable = typeof onSelect === 'function';

  if (showSkeleton) {
    return (
      <Stack spacing={1} sx={{ py: 0.5 }}>
        <Box sx={{ height: 28, width: '100%', borderRadius: '8px', ...shimmer }} />
        <Box sx={{ height: 28, width: '80%', borderRadius: '8px', ...shimmer }} />
      </Stack>
    );
  }

  if (balances.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: brand.muted, py: 0.5 }}>
        No currencies available yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 0.75 : 1.25} data-testid="customer-balance-list">
      {balances.map((b) => {
        const amountLabel = formatBalanceAmount(b, displayInUsd);
        const selected = selectedMintId != null && b.id === String(selectedMintId);

        return (
          <Box
            key={b.id}
            component={selectable ? 'button' : 'div'}
            type={selectable ? 'button' : undefined}
            onClick={selectable ? () => onSelect(b) : undefined}
            data-selected={selected ? 'true' : undefined}
            aria-pressed={selectable ? selected : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: compact ? 1 : 1.5,
              py: compact ? 0.75 : 1.25,
              borderRadius: '10px',
              backgroundColor: selected ? brand.goldWash : brand.canvasSunk,
              border: `1px solid ${selected ? brand.gold : brand.hairline}`,
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
              width: '100%',
              cursor: selectable ? 'pointer' : 'default',
              '&:hover': selectable
                ? {
                    backgroundColor: selected ? brand.goldWash : brand.hairlineSoft,
                    borderColor: selected ? brand.gold : brand.border,
                  }
                : undefined,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              <MintAvatar src={b.image} symbol={b.symbol} size={avatarSize} />
              <Box
                component="span"
                sx={{
                  fontSize: compact ? 12.5 : 14,
                  fontWeight: 700,
                  color: brand.body,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {b.label}
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: compact ? 13 : 15,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: brand.ink,
                whiteSpace: 'nowrap',
                flex: 'none',
                minWidth: displayInUsd ? 72 : 108,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {amountLabel}
            </Box>
          </Box>
        );
      })}
      {error ? (
        <Typography sx={{ fontSize: 11.5, color: brand.warnFg }}>
          Refresh issue: {error}
        </Typography>
      ) : null}
    </Stack>
  );
}
